import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users, Loader2, Download, Eye, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtTz } from "../lib/time";

export default function AdminToolboxTalks() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [talks, setTalks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [formData, setFormData] = useState({
    date: '',
    project_id: '',
    site: '',
    topic: '',
    presenter: '',
    key_points: '',
    action_items: '',
    attendees: [{ name: '', signature: '' }],
  });

  const fetchData = useCallback(async () => {
    try {
      const [talksRes, projRes] = await Promise.all([
        authAxios.get('/forms/toolbox-talks'),
        authAxios.get('/projects'),
      ]);
      setTalks(talksRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => setFormData({
    date: '', project_id: '', site: '', topic: '', presenter: '',
    key_points: '', action_items: '', attendees: [{ name: '', signature: '' }],
  });

  const addAttendee = () => setFormData({ ...formData, attendees: [...formData.attendees, { name: '', signature: '' }] });
  const removeAttendee = (i) => setFormData({ ...formData, attendees: formData.attendees.filter((_, idx) => idx !== i) });
  const updateAttendee = (i, field, val) => {
    const arr = [...formData.attendees];
    arr[i] = { ...arr[i], [field]: val };
    setFormData({ ...formData, attendees: arr });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date || !formData.site || !formData.topic || !formData.presenter || !formData.key_points) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null,
        attendees: formData.attendees.filter(a => a.name.trim()),
      };
      await authAxios.post('/forms/toolbox-talks', payload);
      toast.success('Toolbox talk submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (id) => {
    try {
      const res = await authAxios.get(`/pdf/toolbox-talk/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `toolbox_talk_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this toolbox talk?')) return;
    try {
      await authAxios.delete(`/forms/toolbox-talks/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="toolbox-talks">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Toolbox Talks</h1>
          <p className="text-stone-500 mt-1">Record daily safety meetings and attendee sign-off</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-toolbox-btn">
              <Plus className="w-5 h-5 mr-2" />New Toolbox Talk
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />Toolbox Talk
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Meeting Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="toolbox-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>Project (optional)</Label>
                    <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                      <SelectTrigger data-testid="toolbox-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Site / Location *</Label>
                  <Input value={formData.site} onChange={(e) => setFormData({ ...formData, site: e.target.value })} placeholder="e.g. North Yard, Block B" required data-testid="toolbox-site" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Topic *</Label>
                    <Input value={formData.topic} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} placeholder="e.g. Ladder Safety" required data-testid="toolbox-topic" />
                  </div>
                  <div className="space-y-2">
                    <Label>Presenter *</Label>
                    <Input value={formData.presenter} onChange={(e) => setFormData({ ...formData, presenter: e.target.value })} placeholder="Name of person leading the talk" required data-testid="toolbox-presenter" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Key Points / Hazards Discussed *</Label>
                <Textarea value={formData.key_points} onChange={(e) => setFormData({ ...formData, key_points: e.target.value })} rows={4} placeholder="Summarise what was covered..." required data-testid="toolbox-keypoints" />
              </div>

              <div className="space-y-2">
                <Label>Action Items / Follow-ups</Label>
                <Textarea value={formData.action_items} onChange={(e) => setFormData({ ...formData, action_items: e.target.value })} rows={2} placeholder="What needs to be done or revisited" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-semibold text-stone-700">Attendees</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addAttendee} data-testid="add-attendee-btn">
                    <Plus className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
                {formData.attendees.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input value={a.name} onChange={(e) => updateAttendee(i, 'name', e.target.value)} placeholder="Full name" data-testid={`attendee-name-${i}`} />
                    </div>
                    <div className="col-span-6 space-y-1">
                      <Label className="text-xs">Signature (type name)</Label>
                      <Input value={a.signature} onChange={(e) => updateAttendee(i, 'signature', e.target.value)} placeholder="Type to sign" data-testid={`attendee-sig-${i}`} />
                    </div>
                    <div className="col-span-1">
                      {formData.attendees.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeAttendee(i)}>
                          <X className="w-4 h-4 text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500" data-testid="submit-toolbox-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Submit
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />Records ({talks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
          ) : talks.length === 0 ? (
            <div className="text-center py-12 text-stone-500"><Users className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No toolbox talks recorded yet</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Presenter</TableHead>
                    <TableHead>Attendees</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {talks.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>{fmtTz(t.date, 'MMM d, yyyy')}</TableCell>
                      <TableCell className="font-medium">{t.topic}</TableCell>
                      <TableCell>{t.site}</TableCell>
                      <TableCell>{t.presenter}</TableCell>
                      <TableCell><Badge variant="outline">{t.attendees?.length || 0}</Badge></TableCell>
                      <TableCell>{t.submitted_by_name}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => { setSelected(t); setViewOpen(true); }} data-testid={`view-toolbox-${t.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => exportPDF(t.id)} data-testid={`pdf-toolbox-${t.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} data-testid={`delete-toolbox-${t.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />Toolbox Talk Details
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-stone-500">Date</p><p className="font-medium">{fmtTz(selected.date, 'MMM d, yyyy')}</p></div>
                <div><p className="text-stone-500">Project</p><p className="font-medium">{selected.project_name || '-'}</p></div>
                <div><p className="text-stone-500">Site</p><p className="font-medium">{selected.site}</p></div>
                <div><p className="text-stone-500">Topic</p><p className="font-medium">{selected.topic}</p></div>
                <div><p className="text-stone-500">Presenter</p><p className="font-medium">{selected.presenter}</p></div>
                <div><p className="text-stone-500">Submitted By</p><p className="font-medium">{selected.submitted_by_name}</p></div>
              </div>
              <div className="text-sm"><p className="text-stone-500">Key Points</p><p className="whitespace-pre-wrap">{selected.key_points}</p></div>
              {selected.action_items && <div className="text-sm"><p className="text-stone-500">Action Items</p><p className="whitespace-pre-wrap">{selected.action_items}</p></div>}
              {selected.attendees?.length > 0 && (
                <div className="text-sm">
                  <p className="text-stone-500 mb-2">Attendees ({selected.attendees.length})</p>
                  <div className="space-y-1">
                    {selected.attendees.map((a, i) => (
                      <div key={i} className="flex justify-between bg-stone-50 rounded px-3 py-2">
                        <span className="font-medium">{a.name}</span>
                        <span className="italic text-stone-600">{a.signature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
